/**
 * The booking to pay (docs/NEW-UI/07 D-01..D-03). Two looks of the same props:
 *  - standard  the generic read-back (platform Summary) with the rows it has always had;
 *  - classic   the old kiosk's card (backend/frontend/src/components/touch/top-bar/PaymentCard.tsx): the train with
 *              its times and stations, the cost detail with the chips and the unit price, the time left.
 * The buttons are the bar's (the spec's `bar:`, D-06); the time left is shown only, as on the old kiosk.
 */
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { formatMoney } from '@platform/i18n/translator';
import { Icon } from '@platform/ui-kit/Icon';
import { summary } from '@platform/widgets/generic/Summary';
import { defineWidget, type WidgetDefinition, type WidgetProps } from '@platform/widgets/contract';
import { classOnly, classicDuration, changesLabel, extraChips, journeySchema, onBoardIcons, paxLabel } from './trip';
import { journeyItinerary } from './itinerary';
import { ItineraryCover } from './TrainItinerary';
import css from './TripCards.module.css';

type SummaryView = typeof summary extends WidgetDefinition<infer P> ? P : never;

const schema = z.object({
  /** What the standard look shows: the generic summary's props, as the builder has always made them. */
  view: summary.schema as z.ZodType<SummaryView>,
  trip: journeySchema.extend({
    unitPrice: z.number().nullable(),
    total: z.number().nullable(),
    /** The old kiosk's countdown, from the moment the screen appears. */
    timeoutSeconds: z.number().default(120),
  }),
});

type Props = z.infer<typeof schema>;


function Countdown({ seconds, t }: { seconds: number; t: WidgetProps<Props>['ctx']['t'] }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const tick = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(tick);
  }, []);
  const time = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  return <p className={`${css.countdown} ${left < 30 ? css.urgent : ''}`} role="timer">{t('trains.summary.time_left', { time })}</p>;
}

function ClassicSummary({ props, ctx }: WidgetProps<Props>) {
  const { t, language } = ctx;
  const { trip } = props;
  const money = (n: number | null) => (n === null ? '—' : formatMoney(n, language));
  const pax = trip.adults + trip.children;
  const onBoard = onBoardIcons(trip.onBoard);
  const itinerary = journeyItinerary(trip);
  return (
    <section className={`${css.card} ${css.summaryCard}`} aria-label={t('trains.summary.classic_title')}>
      <header className={css.header}><h2>{t('trains.summary.classic_title')}</h2></header>
      <div className={css.body}>
        <div className={css.train}>
          <div className={css.timeline} aria-hidden>
            <Icon name="train" size={26} />
            <span className={css.line} />
            <span className={css.dot} />
          </div>
          <div className={css.stops}>
            <p className={css.stop}><strong className={css.time}>{trip.departure}</strong><strong>{trip.origin}</strong></p>
            <p className={css.duration}>{t('trains.summary.duration', { duration: classicDuration(trip.durationMin), changes: changesLabel(trip.changes, t) })}</p>
            <p className={css.stop}><strong className={css.time}>{trip.arrival}</strong><strong>{trip.destination}</strong></p>
          </div>
          <div className={css.number}>
            <span className={css.badge}>{trip.trainType}</span>
            <strong>{trip.trainNumber}</strong>
          </div>
          {onBoard.length > 0 && (
            <div className={css.onBoard} aria-hidden>
              {onBoard.map((name) => <Icon key={name} name={name} size={26} />)}
            </div>
          )}
        </div>

        <div>
          <p className={css.costsTitle}>{t('trains.summary.cost_detail')}</p>
          <div className={css.chips}>
            {trip.travelClass && <span className={css.chip}><Icon name="class" size={22} />{t('trains.summary.class_chip', { class: classOnly(trip.travelClass) })}</span>}
            {trip.seats.map((s) => (
              <span key={`${s.coach}-${s.seat}`} className={css.chip}><Icon name="seat" size={22} />{t('trains.summary.seat_chip', { seat: s.seat, coach: s.coach })}</span>
            ))}
            <span className={css.chip}><Icon name="users" size={22} />{paxLabel(pax, t)}</span>
            {extraChips(trip.extras, t).map((x) => (
              <span key={x.key} className={`${css.chip} ${css.extra}`}><Icon name={x.icon} size={22} />{x.label}</span>
            ))}
          </div>
          <div className={css.prices}>
            <p><span>{t('trains.summary.unit_price')}</span><span>{money(trip.unitPrice)}</span></p>
            <p><span>{t('trains.summary.passengers_count')}</span><span>× {pax}</span></p>
            <p className={css.totalRow}><strong>{t('trains.summary.total')}</strong><strong className={css.totalValue}>{money(trip.total)}</strong></p>
          </div>
        </div>

        <Countdown seconds={trip.timeoutSeconds} t={t} />
      </div>
      {/* the changes are words here, as on the old kiosk: asked by voice («che cambi ha?»), the itinerary covers it */}
      {itinerary && <ItineraryCover of={itinerary} t={t} closeLabel={t('overlay.close')} look="cards" headerClassName={css.coverHead} />}
    </section>
  );
}

export const tripSummary = defineWidget({
  key: 'trip-summary',
  schema,
  variants: [{ id: 'classic', label: 'widget.variant.classic', Component: ClassicSummary }],
  Component: ({ props, ctx, api }) => <summary.Component props={props.view} ctx={ctx} api={api} />,
});
